// src/components/lessons/ExercisePlayer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Play, Pause, Volume2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { saveToR2 } from '@/api/r2';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// ✅ helper: normalizza sempre le domande strutturate (array o stringa JSON)
const getStructuredQuestions = (lesson) => {
  const raw = lesson?.exercise_structured_questions;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const arr = JSON.parse(raw); return Array.isArray(arr) ? arr : []; } catch { return []; }
  }
  return [];
};
// ✅ alias logico per il nuovo tipo
const isStructuredType = (t) => t === 'structured_questionnaire' || t === 'goals_rewards';

export default function ExercisePlayer({ lesson, previousAnswer, onClose, onSubmit }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [timerDuration, setTimerDuration] = useState(10 * 60); // 10 min
  const [timeLeft, setTimeLeft] = useState(10 * 60);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scored test
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allQuestions, setAllQuestions] = useState([]);
  const [totalScore, setTotalScore] = useState(0);

  // Tracking
  const [exerciseStartTime, setExerciseStartTime] = useState(Date.now());
  const [backgroundMusic, setBackgroundMusic] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  const backgroundAudioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    setExerciseStartTime(Date.now());
    setTimeLeft(timerDuration);

    if (previousAnswer) setRetryCount(r => r + 1);
    else setRetryCount(0);

    if (lesson.exercise_type === 'scored_test') {
      const mc = (lesson.scored_test_multiple_choice || []).map((q, i) => ({
        ...q,
        type: 'multiple_choice',
        index: i,
        question: q.question,
        options: q.options,
      }));
      const open = (lesson.scored_test_open_questions || []).map((q, i) => ({
        question: q,
        type: 'open_text',
        index: i + mc.length,
      }));
      setAllQuestions([...mc, ...open]);
      setAnswers({});
      setCurrentQuestionIndex(0);
      setTotalScore(0);
    }

    // ✅ inizializzazione risposte anche per goals_rewards
    if (isStructuredType(lesson.exercise_type)) {
      let initial = {};
      if (previousAnswer) {
        try { initial = JSON.parse(previousAnswer); } catch {}
      }
      setAnswers(initial);
    } else if (lesson.exercise_type !== 'scored_test') {
      setAnswers({ simple: previousAnswer || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  // Timer
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setIsPlaying(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isPlaying]);

  // Conteggio parole
  useEffect(() => {
    let text = '';
    // ✅ conteggio anche per goals_rewards
    if (isStructuredType(lesson.exercise_type)) {
      Object.values(answers).forEach(v => { if (typeof v === 'string') text += v + ' '; });
    } else {
      text = answers.simple || '';
    }
    const words = text.trim().split(/\s+/).filter(Boolean);
    setWordCount(words.length);
  }, [answers, lesson.exercise_type]);

  const togglePlayPause = () => {
    if (!isPlaying && timeLeft === 0 && lesson.exercise_type === 'breathing') {
      setTimeLeft(timerDuration);
    }
    setIsPlaying(p => !p);
  };

  const handleBackgroundMusicChange = (url) => {
    setBackgroundMusic(url);
    if (!backgroundAudioRef.current) return;
    if (url) {
      backgroundAudioRef.current.src = url;
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.volume = 0.3;
      backgroundAudioRef.current.play().catch(() => {});
    } else {
      backgroundAudioRef.current.pause();
      backgroundAudioRef.current.src = '';
    }
  };

  // ---- Salvataggio su R2: esercizi NON scored ----
  const handleSubmit = async () => {
    setIsSubmitting(true);
    const completionTime = Math.floor((Date.now() - exerciseStartTime) / 1000);

    let finalAnswerToSubmit;
    let textForCount = '';

    // ✅ salva come JSON anche per goals_rewards
    if (isStructuredType(lesson.exercise_type)) {
      finalAnswerToSubmit = JSON.stringify(answers);
      Object.values(answers).forEach(v => { if (typeof v === 'string') textForCount += v + ' '; });
    } else {
      finalAnswerToSubmit = answers.simple;
      textForCount = answers.simple || '';
    }

    const totalWords = textForCount.trim().split(/\s+/).filter(Boolean).length;

    const payload = {
      lesson_id: lesson.id,
      data: {
        lesson_id: lesson.id,
        lesson_title: lesson.title,
        exercise_type: lesson.exercise_type,
        completion_date: new Date().toISOString(),
        completion_time_seconds: completionTime,
        word_count: totalWords,
        background_music_used: backgroundMusic || null,
        retry_count: retryCount,
        answer_data: finalAnswerToSubmit,
        exercise_instructions: lesson.exercise_instructions,
        was_retry: retryCount > 0,
        breathing_duration_seconds: lesson.exercise_type === 'breathing' ? timerDuration : undefined,
      },
      meta: { source: 'exercise_player' },
    };

    try {
      await saveToR2('exercise_result', payload);
    } catch (err) {
      console.warn('[R2] exercise_result queued/offline', err);
    }

    try {
      await onSubmit(finalAnswerToSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Scored test helpers ----
  const handleStructuredAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleScoredTestAnswer = (answer) => {
    const q = allQuestions[currentQuestionIndex];
    let score = 0;
    if (q.type === 'multiple_choice') {
      const idx = q.options.indexOf(answer);
      score = q.options.length - idx; // semplice scoring decrescente
      setTotalScore(prev => prev + score);
    }

    setAnswers(prev => ({
      ...prev,
      [`question_${q.index}`]: answer,
      [`score_${q.index}`]: score,
    }));

    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex(i => i + 1);
    } else {
      handleSubmitScoredTest();
    }
  };

  const getDependencyLevel = (score, maxScore) => {
    if (maxScore === 0) return 'N/A';
    const p = (score / maxScore) * 100;
    if (p >= 80) return 'Alto';
    if (p >= 60) return 'Medio-Alto';
    if (p >= 40) return 'Medio';
    if (p >= 20) return 'Basso-Medio';
    return 'Basso';
  };

  // ---- Salvataggio su R2: TEST con punteggio ----
  const handleSubmitScoredTest = async () => {
    setIsSubmitting(true);

    const maxPossibleScore =
      (lesson.scored_test_multiple_choice || [])
        .reduce((sum, q) => sum + (q.options?.length || 0), 0);

    const result = {
      answers,
      totalScore,
      maxPossibleScore,
      dependencyLevel: getDependencyLevel(totalScore, maxPossibleScore),
    };

    const completionTime = Math.floor((Date.now() - exerciseStartTime) / 1000);
    const textForCount = Object.values(answers)
      .reduce((acc, v) => (typeof v === 'string' ? acc + v + ' ' : acc), '');
    const totalWords = textForCount.trim().split(/\s+/).filter(Boolean).length;

    const payload = {
      lesson_id: lesson.id,
      data: {
        lesson_id: lesson.id,
        lesson_title: lesson.title,
        exercise_type: lesson.exercise_type,
        completion_date: new Date().toISOString(),
        completion_time_seconds: completionTime,
        word_count: totalWords,
        background_music_used: backgroundMusic || null,
        retry_count: retryCount,
        answer_data: JSON.stringify(result),
        exercise_instructions: lesson.exercise_instructions,
        was_retry: retryCount > 0,
      },
      meta: { source: 'exercise_player_scored' },
    };

    try {
      await saveToR2('exercise_result', payload);
    } catch (err) {
      console.warn('[R2] exercise_result (scored) queued/offline', err);
    }

    try {
      await onSubmit(JSON.stringify(result));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStructuredQuestionnaire = () => {
    const questions = getStructuredQuestions(lesson); // ✅ usa parsing sicuro
    return (
      <ScrollArea className="h-[450px] p-4 border rounded-lg bg-gray-50">
        <div className="space-y-6">
          {questions.map((q) => {
            if (q.type === 'header') {
              return (
                <h4
                  key={q.id}
                  className="text-lg font-bold text-gray-800 mt-6 pt-4 border-t first:mt-0 first:pt-0 first:border-t-0"
                >
                  {q.question}
                </h4>
              );
            }
            if (q.type === 'text') {
              return (
                <div key={q.id}>
                  <Label htmlFor={q.id} className="font-medium">{q.question}</Label>
                  <Textarea
                    id={q.id}
                    value={answers[q.id] || ''}
                    onChange={(e) => handleStructuredAnswerChange(q.id, e.target.value)}
                    className="mt-2"
                  />
                </div>
              );
            }
            if (q.type === 'radio') {
              return (
                <div key={q.id}>
                  <Label className="font-medium">{q.question}</Label>
                  <RadioGroup
                    value={answers[q.id]}
                    onValueChange={(value) => handleStructuredAnswerChange(q.id, value)}
                    className="mt-2 space-y-1"
                  >
                    {(q.options || []).map((opt, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100">
                        <RadioGroupItem value={opt} id={`${q.id}-${index}`} />
                        <Label htmlFor={`${q.id}-${index}`} className="font-normal cursor-pointer w-full">
                          {opt}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              );
            }
            return null;
          })}
        </div>
      </ScrollArea>
    );
  };

  const renderScoredTest = () => {
    if (currentQuestionIndex >= allQuestions.length) {
      const maxPossibleScore =
        (lesson.scored_test_multiple_choice || [])
          .reduce((sum, q) => sum + (q.options?.length || 0), 0);

      return (
        <div className="text-center space-y-6">
          <h3 className="text-2xl font-bold text-green-600">Test Completato!</h3>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-lg">
              Punteggio Totale: <span className="font-bold">{totalScore}</span>
            </p>
            <p className="text-sm text-gray-600">
              Livello di dipendenza: {getDependencyLevel(totalScore, maxPossibleScore)}
            </p>
          </div>
          <Button onClick={onClose} size="lg">Chiudi</Button>
        </div>
      );
    }

    const q = allQuestions[currentQuestionIndex];

    return (
      <div className="space-y-6">
        <div className="text-center">
          <p className="text-sm text-gray-500">
            Domanda {currentQuestionIndex + 1} di {allQuestions.length}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / allQuestions.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="p-6 bg-gray-50 rounded-lg">
          <h4 className="text-lg font-semibold mb-4">{q.question}</h4>

          {q.type === 'multiple_choice' ? (
            <div className="space-y-3">
              {q.options.map((opt, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="w-full text-left justify-start p-4 h-auto"
                  onClick={() => handleScoredTestAnswer(opt)}
                >
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium mr-3">
                    {String.fromCharCode(97 + index)}
                  </span>
                  {opt}
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <Textarea
                value={answers[`question_${q.index}`] || ''}
                onChange={(e) =>
                  setAnswers(prev => ({ ...prev, [`question_${q.index}`]: e.target.value }))
                }
                placeholder="Scrivi la tua risposta..."
                rows={4}
              />
              <Button
                onClick={() =>
                  handleScoredTestAnswer(answers[`question_${q.index}`] || '')
                }
                className="w-full"
                disabled={!answers[`question_${q.index}`]?.trim()}
              >
                Prossima Domanda
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderExerciseContent = () => {
    if (lesson.exercise_type === 'scored_test') return renderScoredTest();

    return (
      <div className="space-y-4">
        {/* Timer */}
        <div className="text-center p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-500">Tempo Rimanente</p>
          <p className="text-5xl font-bold">{formatTime(timeLeft)}</p>
        </div>

        {/* Timer controls (breathing) + Play/Pause */}
        <div className="flex justify-center items-center gap-4">
          {lesson.exercise_type === 'breathing' && (
            <>
              <Label>Durata: {timerDuration / 60} min</Label>
              <Slider
                value={[timerDuration]}
                onValueChange={([v]) => { setTimerDuration(v); setTimeLeft(v); }}
                min={60}
                max={1800}
                step={60}
                className="w-48"
                disabled={isPlaying}
              />
            </>
          )}
          <Button onClick={togglePlayPause} size="lg" className="rounded-full w-20 h-20">
            {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
          </Button>
        </div>

        {/* Prompt */}
        {lesson.exercise_question && (
          <p className="font-semibold text-lg text-center">{lesson.exercise_question}</p>
        )}

        {/* Inputs */}
        {(() => {
          if (lesson.order === 5 && previousAnswer) {
            return (
              <div className="p-4 border rounded-lg bg-gray-50 max-h-64 overflow-y-auto">
                <h4 className="font-semibold mb-2">La tua lettera:</h4>
                <p className="whitespace-pre-wrap">{previousAnswer}</p>
              </div>
            );
          }
          // ✅ usa il renderer strutturato anche per goals_rewards
          if (isStructuredType(lesson.exercise_type)) {
            return renderStructuredQuestionnaire();
          }
          switch (lesson.exercise_type) {
            case 'breathing':
              return <p className="text-center text-lg text-gray-600">Concentrati sul tuo respiro.</p>;
            case 'multiple_choice':
              return (
                <RadioGroup value={answers.simple} onValueChange={v => setAnswers({ simple: v })}>
                  {lesson.exercise_options?.map((opt, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <RadioGroupItem value={opt} id={`ex-opt-${idx}`} />
                      <Label htmlFor={`ex-opt-${idx}`}>{opt}</Label>
                    </div>
                  ))}
                </RadioGroup>
              );
            case 'reflection':
            case 'text_input':
            default:
              return (
                <Textarea
                  value={answers.simple || ''}
                  onChange={e => setAnswers({ simple: e.target.value })}
                  placeholder="Inizia a scrivere..."
                  rows={10}
                />
              );
          }
        })()}
      </div>
    );
  };

  const isAnswerValid = () => {
    if (lesson.order === 5 && previousAnswer) return false;

    // ✅ validazione anche per goals_rewards
    if (isStructuredType(lesson.exercise_type)) {
      return Object.keys(answers).length > 0 &&
        Object.values(answers).some(
          val => (typeof val === 'string' && val.trim() !== '') || (val !== undefined && val !== null)
        );
    }
    if (lesson.exercise_type === 'reflection' || lesson.exercise_type === 'text_input') {
      return (answers.simple || '').trim().length > 0;
    }
    if (lesson.exercise_type === 'multiple_choice') {
      return answers.simple != null && answers.simple !== '';
    }
    if (lesson.exercise_type === 'breathing') return true;
    return true;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      /* 🔧 FIX: chiudi SOLO se il mousedown è sull'overlay, non sul contenuto.
         Questo evita la chiusura immediata dopo il click sul bottone "Inizia". */
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      tabIndex={-1}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        // impedisci che il mousedown “risalga” all’overlay
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Esercizio</h2>
              <p className="text-gray-600">{lesson.title}</p>
              {retryCount > 0 && (
                <p className="text-orange-600 text-sm mt-1">Tentativo #{retryCount + 1}</p>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Background Music */}
          {lesson.background_audio_options?.length > 0 && (
            <div className="mb-6 p-4 border rounded-lg bg-blue-50">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Volume2 className="w-4 h-4" />
                Sottofondo Musicale (Opzionale)
              </h3>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={backgroundMusic === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleBackgroundMusicChange('')}
                >
                  Nessun sottofondo
                </Button>
                {lesson.background_audio_options.map((opt, i) => (
                  <Button
                    key={i}
                    variant={backgroundMusic === opt.url ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleBackgroundMusicChange(opt.url)}
                  >
                    {opt.name}
                  </Button>
                ))}
              </div>
              <audio ref={backgroundAudioRef} />
            </div>
          )}

          {/* Instructions */}
          {lesson.exercise_instructions && (
            <div className="mb-6 p-4 border rounded-lg bg-gray-50">
              <h3 className="font-medium mb-2">Istruzioni</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{lesson.exercise_instructions}</p>
            </div>
          )}

          {/* Content */}
          {renderExerciseContent()}

          {/* Word Count */}
          {((lesson.exercise_type === 'text_input' || lesson.exercise_type === 'reflection') && wordCount > 0) && (
            <div className="mt-2 text-sm text-gray-500">Parole scritte: {wordCount}</div>
          )}

          {/* Footer (no footer for scored_test) */}
          {lesson.exercise_type !== 'scored_test' && (
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !isAnswerValid()}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
              >
                <Send className="w-4 h-4 mr-2" />
                {isSubmitting ? 'Invio in corso...' : (retryCount > 0 ? 'Aggiorna Risposta' : 'Completa Esercizio')}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
